import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import { ROLE_OPTIONS, todayIso } from './shared';

/** Add or edit a staff member. */
function StaffFormDialog(props) {
    const { open, onClose, onSaved, showErrorMessage, member } = props;
    const isMobile = useMediaQuery('(max-width:600px)');
    const editing = Boolean(member);

    const BLANK = {
        name: '', phone: '', role: 'cook', monthly_salary: '',
        joined_on: todayIso(), is_active: true,
        employee_code: '', designation: '', pan_number: '', user: '',
        extra_hold_days: '0', annual_leave_days: '', opening_leave_balance: '0',
        document_password: '',
    };

    const [form, setForm] = useState(BLANK);
    const [logins, setLogins] = useState([]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(member ? {
            name: member.name || '',
            phone: member.phone || '',
            role: member.role || 'cook',
            monthly_salary: String(member.monthly_salary ?? ''),
            joined_on: member.joined_on || todayIso(),
            left_on: member.left_on || '',
            is_active: member.is_active !== false,
            employee_code: member.employee_code || '',
            designation: member.designation || '',
            pan_number: member.pan_number || '',
            user: member.user || '',
            extra_hold_days: String(member.extra_hold_days ?? 0),
            // Null means "use the business default", which must round-trip as an
            // empty box rather than as the number 0.
            annual_leave_days: member.annual_leave_days == null
                ? '' : String(member.annual_leave_days),
            opening_leave_balance: String(member.opening_leave_balance ?? 0),
            document_password: member.document_password || '',
        } : BLANK);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, member]);

    useEffect(() => {
        if (!open) return;
        // Which logins exist, so this person can be linked to theirs. Failing to
        // load them is not fatal - the rest of the form still works, the link
        // just cannot be set here.
        api.get('/staff/logins/')
            .then((res) => setLogins(res.data.logins || []))
            .catch(() => setLogins([]));
    }, [open]);

    const set = (field) => (event) => {
        const value = event.target.type === 'checkbox'
            ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const validate = () => {
        const found = {};
        if (!form.name.trim()) found.name = 'Name is required';
        const salary = Number(form.monthly_salary);
        if (form.monthly_salary === '' || Number.isNaN(salary) || salary < 0) {
            found.monthly_salary = 'Enter a salary of 0 or more';
        }
        if (!form.joined_on) found.joined_on = 'Joining date is required';
        setErrors(found);
        return Object.keys(found).length === 0;
    };

    const submit = () => {
        if (!validate()) return;
        setSaving(true);
        const payload = { ...form };
        // An empty date must be null, not '', or the API rejects it.
        if (!payload.left_on) delete payload.left_on;
        // '' means "no login linked", which the API expects as null.
        payload.user = payload.user || null;
        // Blank annual leave means "use the business default", which is null.
        payload.annual_leave_days = payload.annual_leave_days === ''
            ? null : payload.annual_leave_days;

        const request = editing
            ? api.patch(`/staff/${member.id}/`, payload)
            : api.post('/staff/', payload);

        request.then(() => onSaved())
            .catch((error) => {
                // Put the server's complaint on the field it is about. A
                // password rejected for being too short belongs under the
                // password box, not in a snackbar that says "400".
                const fields = error?.response?.data;
                if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
                    const found = {};
                    Object.entries(fields).forEach(([name, message]) => {
                        if (name in form) {
                            found[name] = Array.isArray(message) ? message[0] : message;
                        }
                    });
                    if (Object.keys(found).length) {
                        setErrors(found);
                        return;
                    }
                }
                showErrorMessage(getErrorMessage(error));
            })
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose}
            fullWidth maxWidth="sm" fullScreen={isMobile}>
            <DialogTitle>{editing ? 'Edit staff member' : 'Add staff member'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField label="Name" fullWidth value={form.name}
                            onChange={set('name')} error={Boolean(errors.name)}
                            helperText={errors.name} autoFocus />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel id="role-label">Role</InputLabel>
                            <Select labelId="role-label" label="Role"
                                value={form.role} onChange={set('role')}>
                                {ROLE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label="Phone" fullWidth value={form.phone}
                            onChange={set('phone')} inputMode="tel" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label="Monthly salary" fullWidth
                            value={form.monthly_salary} onChange={set('monthly_salary')}
                            type="number" inputMode="decimal"
                            error={Boolean(errors.monthly_salary)}
                            helperText={errors.monthly_salary} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label="Joined on" fullWidth type="date"
                            value={form.joined_on} onChange={set('joined_on')}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(errors.joined_on)} helperText={errors.joined_on} />
                    </Grid>

                    {/* Printed on the payslip. Optional, because someone can be
                        hired and paid before their paperwork catches up. */}
                    <Grid size={{ xs: 12 }}>
                        <Divider textAlign="left" sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                For the payslip
                            </Typography>
                        </Divider>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField label="Employee no." fullWidth
                            value={form.employee_code} onChange={set('employee_code')}
                            placeholder="DA021"
                            error={Boolean(errors.employee_code)}
                            helperText={errors.employee_code} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField label="Designation" fullWidth
                            value={form.designation} onChange={set('designation')}
                            placeholder="Chef" helperText="Defaults to the role" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField label="PAN" fullWidth value={form.pan_number}
                            onChange={set('pan_number')}
                            inputProps={{ style: { textTransform: 'uppercase' } }} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Divider textAlign="left" sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Hold and leave
                            </Typography>
                        </Divider>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField label="Extra days held" fullWidth
                            inputMode="numeric" value={form.extra_hold_days}
                            onFocus={(e) => e.target.select()}
                            onChange={set('extra_hold_days')}
                            helperText="On top of the business figure" />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField label="Annual leave" fullWidth
                            inputMode="decimal" value={form.annual_leave_days}
                            onFocus={(e) => e.target.select()}
                            onChange={set('annual_leave_days')}
                            placeholder="default"
                            helperText="Blank uses the default" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField label="Leave carried in" fullWidth
                            inputMode="decimal" value={form.opening_leave_balance}
                            onFocus={(e) => e.target.select()}
                            onChange={set('opening_leave_balance')}
                            helperText="Days already owed" />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        {/* Shown in the clear because an owner has to be able to
                            read it back and tell the person - it locks a file,
                            it is not a login. */}
                        <TextField label="Payslip password" fullWidth
                            value={form.document_password}
                            onChange={set('document_password')}
                            error={Boolean(errors.document_password)}
                            helperText={errors.document_password
                                || 'Locks their payslip files so a forwarded '
                                   + 'payslip stays private. Tell them this '
                                   + 'password. Never reuse a login password.'} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth>
                            <InputLabel id="login-label">Login</InputLabel>
                            <Select labelId="login-label" label="Login"
                                value={form.user || ''} onChange={set('user')}>
                                <MenuItem value="">
                                    <em>No login — cannot sign in</em>
                                </MenuItem>
                                {logins.map((login) => (
                                    <MenuItem key={login.id} value={login.id}
                                        disabled={login.linked_to
                                            && login.linked_to !== (member?.id ?? null)}
                                        sx={{ whiteSpace: 'normal' }}>
                                        {login.label}
                                        {login.linked_to
                                            && login.linked_to !== (member?.id ?? null)
                                            ? ' — taken' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}>
                            Linking a login lets this person see their own leave,
                            advances and payslips, and ask for time off.
                        </Typography>
                    </Grid>
                    {editing && (
                        <>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Left on" fullWidth type="date"
                                    value={form.left_on || ''} onChange={set('left_on')}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Leave blank if still employed" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControlLabel
                                    control={<Switch checked={form.is_active}
                                        onChange={set('is_active')} />}
                                    label="Currently employed" />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default StaffFormDialog;
